import client from './client';
import type { UserConfig, UserPublic, CreateUserDto, UpdateUserDto, AppRoute } from './dtos/user.dto';
import { useAuthStore } from '../stores/authStore';

export async function login(username: string, password: string): Promise<{ token: string; user: UserPublic }> {
  const res = await client.post<{ token: string; user: UserPublic }>(`/auth/login`, { username, password });
  const { token, user } = res.data;

  // Single source of truth: write auth state to Zustand store only.
  // The persist middleware automatically serializes to localStorage.
  useAuthStore.getState().setAuth(
    token,
    username,
    user.username || username,
    user.role,
    user.allowedRoutes || [],
  );

  return res.data;
}

export function logout() {
  useAuthStore.getState().clearAuth();
}

export function isLoggedIn(): boolean {
  const state = useAuthStore.getState();
  return !!(state.token || state.userId);
}

export function isAdmin(): boolean {
  return useAuthStore.getState().userRole === 'admin';
}

export function getAllowedRoutes(): AppRoute[] {
  return useAuthStore.getState().allowedRoutes;
}

export function hasAccess(route: AppRoute): boolean {
  if (isAdmin()) return true;
  const allowedRoutes = getAllowedRoutes();
  return allowedRoutes.includes(route);
}

export async function getUserConfig(): Promise<UserConfig> {
  const res = await client.get(`/auth/user`);
  return res.data;
}

export async function getCurrentUser(): Promise<UserPublic> {
  const res = await client.get(`/auth/me`);
  return res.data;
}

export async function getAvailableRoutes(): Promise<AppRoute[]> {
  const res = await client.get<AppRoute[]>(`/auth/routes`);
  return res.data;
}

export async function saveMyRoutes(allowedRoutes: AppRoute[]): Promise<UserPublic> {
  const res = await client.put<UserPublic>('/auth/user/routes', { allowedRoutes });
  // Update store with new allowed routes
  try {
    useAuthStore.getState().updateAllowedRoutes(res.data.allowedRoutes || []);
  } catch (_e) { /* ignored */ }
  return res.data;
}

// Admin-only functions
export async function getAllUsers(): Promise<UserPublic[]> {
  const res = await client.get<UserPublic[]>(`/auth/users`);
  return res.data;
}

export async function createUser(dto: CreateUserDto): Promise<{ success: boolean }> {
  const res = await client.post(`/auth/users`, dto);
  return res.data;
}

export async function updateUser(username: string, dto: UpdateUserDto): Promise<UserPublic> {
  const res = await client.put<UserPublic>(`/auth/users/${encodeURIComponent(username)}`, dto);
  return res.data;
}

export async function deleteUser(username: string): Promise<{ success: boolean }> {
  const res = await client.delete(`/auth/users/${encodeURIComponent(username)}`);
  return res.data;
}
